import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  HELP_GROUPS,
  HELP_QUESTIONS,
  HELP_SECTIONS,
  helpTitle,
  type HelpQuestion,
  type HelpSection,
  type HelpTopic,
} from '../lib/directorHelp.ts';
import { SHORTCUTS, keyParts, type ShortcutId } from '../lib/shortcuts.ts';

/**
 * Die Hilfe gibt es nur in der Regie. Das „?" an den Bloecken fragt deshalb
 * nach diesem Kontext und zeigt ohne ihn nichts -- dieselben Pulte und der
 * Chat stehen auch anderswo, ohne dass dort ein Hilfeknopf auftaucht.
 */
const HelpContext = createContext<((topic?: HelpTopic) => void) | null>(null);
export const HelpProvider = HelpContext.Provider;

/** Das kleine „?" an einem Block: springt zu seinem Abschnitt in der Hilfe. */
export function HelpLink({ topic }: { topic: HelpTopic }) {
  const open = useContext(HelpContext);
  if (!open) return null;
  const label = `Hilfe: ${helpTitle(topic)}`;
  return (
    <button
      type="button"
      className="help-link"
      aria-label={label}
      title={label}
      // Steht oft in einer Ueberschrift, die selbst auf- und zuklappt.
      onClick={(e) => {
        e.stopPropagation();
        open(topic);
      }}
    >
      ?
    </button>
  );
}

/** Wohin die Hilfe springen soll. `nonce` macht auch denselben Abschnitt zweimal hintereinander anspringbar. */
export interface HelpRequest {
  topic: HelpTopic | null;
  nonce: number;
}

const norm = (text: string) => text.toLocaleLowerCase('de');

/** Jedes Wort der Suche muss vorkommen -- „mikro aus" findet so auch „Mikro … aus". */
const matches = (words: string[], ...texts: string[]) => {
  if (!words.length) return true;
  const hay = norm(texts.join(' '));
  return words.every((w) => hay.includes(w));
};

/**
 * Die Hilfe als Seitenleiste ueber der Regie -- keine eigene Seite: wer die
 * Regie verlaesst, verliert die eigene Kamera und die Sprechleitungen. So
 * laeuft alles weiter, waehrend man liest, und die Tastenkuerzel lassen
 * sich gleich ausprobieren.
 */
export function HelpPanel({ request, onClose }: { request: HelpRequest; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [flash, setFlash] = useState<HelpTopic | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const words = useMemo(() => norm(query).split(/\s+/).filter(Boolean), [query]);
  const searching = words.length > 0;

  const jump = (topic: HelpTopic | null) => {
    requestAnimationFrame(() => {
      const target = topic ? bodyRef.current?.querySelector<HTMLElement>(`#help-${topic}`) : null;
      if (target) {
        target.scrollIntoView({ block: 'start' });
        target.focus({ preventScroll: true });
        setFlash(topic);
      } else {
        bodyRef.current?.scrollTo({ top: 0 });
        panelRef.current?.focus({ preventScroll: true });
      }
    });
  };

  // Jeder Aufruf faengt ohne Suche an -- sonst fehlte der Abschnitt, zu dem
  // das „?" springen soll, womoeglich in der Trefferliste.
  useEffect(() => {
    setQuery('');
    jump(request.topic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.nonce]);

  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 1600);
    return () => window.clearTimeout(timer);
  }, [flash]);

  const goTo = (topic: HelpTopic) => {
    setQuery('');
    jump(topic);
  };

  const sections = HELP_SECTIONS.filter((s) =>
    matches(words, s.title, s.intro, ...s.can, ...(s.cannot ?? [])),
  );
  const questions = HELP_QUESTIONS.filter((q) => matches(words, q.q, q.a));
  const shortcuts = SHORTCUTS.filter((s) => matches(words, s.label, keyParts(s.id).join(' '), 'tastenkürzel'));
  const nothing = searching && !sections.length && !questions.length && !shortcuts.length;

  return (
    <aside
      ref={panelRef}
      className="help-panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="help-title"
      tabIndex={-1}
    >
      <header className="help-panel__head">
        <div className="help-panel__title">
          <h2 id="help-title">Hilfe zur Regie</h2>
          <button type="button" className="help-panel__close" onClick={onClose} aria-label="Hilfe schließen" title="Schließen (Esc)">
            ×
          </button>
        </div>
        <input
          type="search"
          className="help-panel__search"
          placeholder="Suchen, z. B. Splitscreen oder Mikro"
          aria-label="Hilfe durchsuchen"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </header>

      <div className="help-panel__body" ref={bodyRef}>
        {!searching && (
          <nav className="help-toc" aria-label="Inhalt">
            {HELP_SECTIONS.map((s) => (
              <button key={s.id} type="button" onClick={() => goTo(s.id)}>
                {s.title}
              </button>
            ))}
            <button type="button" onClick={() => goTo('fragen')}>
              Wenn etwas nicht klappt
            </button>
            <button type="button" onClick={() => goTo('tasten')}>
              Tastenkürzel
            </button>
          </nav>
        )}

        {nothing && <p className="help-empty">Nichts gefunden zu „{query.trim()}“.</p>}

        {sections.map((s) => (
          <SectionView key={s.id} section={s} flash={flash === s.id} />
        ))}

        {questions.length > 0 && (
          <section id="help-fragen" tabIndex={-1} className={`help-section${flash === 'fragen' ? ' is-flash' : ''}`}>
            <h3>Wenn etwas nicht klappt</h3>
            {questions.map((q) => (
              <QuestionView key={q.q} question={q} open={searching} onMore={goTo} />
            ))}
          </section>
        )}

        {shortcuts.length > 0 && (
          <section id="help-tasten" tabIndex={-1} className={`help-section${flash === 'tasten' ? ' is-flash' : ''}`}>
            <h3>Tastenkürzel</h3>
            <p>
              Für die Arbeit an einem Rechner mit Tastatur. Strg ist die Steuerungstaste
              — am Mac die ctrl-Taste, nicht ⌘. Nach jedem Kürzel steht oben kurz, was passiert ist.
            </p>
            {/* Eine Tabelle fuer alle Gruppen -- so stehen die Beschreibungen buendig untereinander. */}
            <table className="help-keys">
              {HELP_GROUPS.map((group) => {
                const rows = shortcuts.filter((s) => s.group === group.id);
                if (!rows.length) return null;
                return (
                  <tbody key={group.id}>
                    <tr className="help-keys__group">
                      <th colSpan={2} scope="colgroup">
                        <h4>{group.title}</h4>
                        <p className="help-keys__note">{group.note}</p>
                      </th>
                    </tr>
                    {rows.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <Keys id={s.id} />
                        </td>
                        <td>{s.label}</td>
                      </tr>
                    ))}
                    {group.id === 'ansicht' && (
                      <tr>
                        <td>
                          <Keys id="escape" />
                        </td>
                        <td>Hilfe schließen</td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
            </table>
            <p className="help-keys__note">
              Bewusst ohne Kürzel: Freigeben und Ablehnen, Trennen, Kamera oder Bildschirm beenden, Werbepause
              starten und Botschaft senden — das betrifft andere Leute oder lässt sich nicht mit einem Tastendruck
              zurücknehmen.
            </p>
          </section>
        )}
      </div>
    </aside>
  );
}

function SectionView({ section, flash }: { section: HelpSection; flash: boolean }) {
  return (
    <section id={`help-${section.id}`} tabIndex={-1} className={`help-section${flash ? ' is-flash' : ''}`}>
      <h3>{section.title}</h3>
      <p>{section.intro}</p>
      <h4>Das kannst du einstellen</h4>
      <ul>
        {section.can.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {section.cannot && (
        <>
          <h4>Das geht nicht</h4>
          <ul className="help-section__limits">
            {section.cannot.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      )}
      {section.keys && <KeyHints ids={section.keys} />}
    </section>
  );
}

/** Die Kuerzel eines Blocks als Zeile unter dem Abschnitt. */
function KeyHints({ ids }: { ids: ShortcutId[] }) {
  return (
    <div className="help-section__keys">
      <span>Tastenkürzel</span>
      {ids.map((id) => (
        <span key={id} className="help-section__key">
          <Keys id={id} /> {SHORTCUTS.find((s) => s.id === id)?.label}
        </span>
      ))}
    </div>
  );
}

function Keys({ id }: { id: ShortcutId | 'escape' }) {
  return (
    <span className="help-kbd">
      {keyParts(id).map((part, i) => (
        <span key={i}>
          {i > 0 && <span aria-hidden>+</span>}
          <kbd>{part}</kbd>
        </span>
      ))}
    </span>
  );
}

function QuestionView({
  question,
  open,
  onMore,
}: {
  question: HelpQuestion;
  open: boolean;
  onMore: (topic: HelpTopic) => void;
}) {
  return (
    // Bei einer Suche aufgeklappt: wer sucht, will die Antwort sehen, nicht noch einmal klicken.
    <details className="help-question" open={open || undefined}>
      <summary>{question.q}</summary>
      <p>{question.a}</p>
      {question.more && (
        <button type="button" className="help-more" onClick={() => onMore(question.more!)}>
          Mehr dazu: {helpTitle(question.more)} →
        </button>
      )}
    </details>
  );
}
